import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  vendorInvoiceAccountingPreviewSchema,
  vendorInvoiceAccountingSplitSaveSchema,
  vendorInvoiceApproveSchema,
  vendorInvoiceDetailSchema,
  type VendorInvoiceAccountingSplitSave,
  type VendorInvoiceApproveValues,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';

const base = '/api/v1/procurement/vendor-invoices';
const keys = ['vendor-invoice-accounting'] as const;
export function useVendorInvoiceAccountingPreview(id: string) {
  return useQuery({
    queryKey: [...keys, id],
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () =>
      apiFetch(`${base}/${id}/accounting-preview`, vendorInvoiceAccountingPreviewSchema),
  });
}
function useInvoiceAction<T>(
  id: string,
  path: string,
  method: 'POST' | 'PUT',
  validate: (body: T) => unknown,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: T) =>
      apiFetch(`${base}/${id}/${path}`, vendorInvoiceDetailSchema, {
        method,
        body: validate(body),
      }),
    onSuccess: async () => {
      await Promise.all(
        [['procurement'], keys, ['ledger'], ['financial-open-items']].map((queryKey) =>
          client.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}
export const useSaveVendorInvoiceAccountingSplits = (id: string) =>
  useInvoiceAction<VendorInvoiceAccountingSplitSave>(id, 'accounting-splits', 'PUT', (body) =>
    vendorInvoiceAccountingSplitSaveSchema.parse(body),
  );
export const useApproveReviewedVendorInvoice = (id: string) =>
  useInvoiceAction<VendorInvoiceApproveValues>(id, 'approve', 'POST', (body) =>
    vendorInvoiceApproveSchema.parse(body),
  );
